// MCHD Room Scheduler - Building List Component
// Displays buildings in a table with filtering and actions

import React, { useState } from 'react';
import {
  Table,
  TextInput,
  Select,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Pagination,
  Stack,
  Paper,
  Button,
  Menu,
  Alert,
} from '@mantine/core';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconFilter,
  IconDots,
  IconBuilding,
} from '@tabler/icons-react';
import { BuildingWithStats } from '../../api/endpoints/buildings';
import { formatBuildingForDisplay, getBuildingStatusColor } from '../../api/endpoints/buildings';

// =============================================================================
// TYPES
// =============================================================================

interface BuildingListProps {
  buildings: BuildingWithStats[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | undefined;
  filters: {
    search: string;
    sortBy: 'name' | 'createdAt' | 'updatedAt';
    sortOrder: 'asc' | 'desc';
  };
  onFilterChange: (filters: Partial<BuildingListProps['filters']>) => void;
  onSelectBuilding: (id: string) => void;
  onEditBuilding: (id: string) => void;
  onDeleteBuilding: (id: string) => void;
  isLoading?: boolean;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Building List Component
 * Displays buildings in a table with filtering, sorting, and actions
 * 
 * @param props - Component props
 * @returns Building list interface
 */
export function BuildingList({
  buildings,
  pagination,
  filters,
  onFilterChange,
  onSelectBuilding,
  onEditBuilding,
  onDeleteBuilding,
  isLoading = false,
}: BuildingListProps): React.ReactElement {
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle search input change
   * @param event - Input change event
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ search: event.currentTarget.value });
  };

  /**
   * Handle status filter change
   * @param value - Filter value
   */
  const handleStatusFilterChange = (value: string | null) => {
    // For now, we'll just log this since we removed isActive from filters
    console.log('Status filter changed:', value);
  };

  /**
   * Handle sort change
   * @param field - Sort field
   */
  const handleSortChange = (field: 'name' | 'createdAt' | 'updatedAt') => {
    const sortOrder = filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    onFilterChange({ sortBy: field, sortOrder });
  };

  /**
   * Handle page change
   * @param page - Page number
   */
  const handlePageChange = (page: number) => {
    // This would be handled by the parent component
    // For now, we'll just log it
    console.log('Page change:', page);
  };

  /**
   * Handle building deletion
   * @param id - Building ID
   */
  const handleDelete = async (id: string) => {
    try {
      await onDeleteBuilding(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting building:', error);
    }
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  if (isLoading) {
    return (
      <Stack align="center" gap="md">
        <Text>Loading buildings...</Text>
      </Stack>
    );
  }

  if (buildings.length === 0) {
    return (
      <Paper p="xl" radius="md">
        <Stack align="center" gap="md">
          <IconBuilding size={48} color="gray" />
          <Text size="lg" color="dimmed">
            No buildings found
          </Text>
          <Text color="dimmed">
            {filters.search
              ? 'Try adjusting your filters to see more results.'
              : 'Create your first building to get started.'}
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Filters */}
      <Paper p="md" radius="md" withBorder>
        <Group gap="md" align="flex-end">
          <TextInput
            placeholder="Search buildings..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={handleSearchChange}
            style={{ flex: 1 }}
          />
          
          <Select
            placeholder="Filter by status"
            data={[
              { value: 'all', label: 'All Buildings' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value="all"
            onChange={handleStatusFilterChange}
            leftSection={<IconFilter size={16} />}
          />
        </Group>
      </Paper>

      {/* Buildings Table */}
      <Paper radius="md" withBorder>
        <Table highlightOnHover>
          <thead>
            <tr>
              <th>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => handleSortChange('name')}
                  rightSection={
                    filters.sortBy === 'name' ? (
                      filters.sortOrder === 'asc' ? '↑' : '↓'
                    ) : null
                  }
                >
                  Building Name
                </Button>
              </th>
              <th>Description</th>
              <th>Status</th>
              <th>Floors</th>
              <th>Rooms</th>
              <th>Holidays</th>
              <th>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => handleSortChange('createdAt')}
                  rightSection={
                    filters.sortBy === 'createdAt' ? (
                      filters.sortOrder === 'asc' ? '↑' : '↓'
                    ) : null
                  }
                >
                  Created
                </Button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {buildings.map((building) => {
              const formatted = formatBuildingForDisplay(building);
              const statusColor = getBuildingStatusColor(building.isActive);
              
              return (
                <tr key={building.id}>
                  <td>
                    <Text fw={500}>{building.name}</Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed" truncate>
                      {formatted.description}
                    </Text>
                  </td>
                  <td>
                    <Badge color={statusColor} variant="light">
                      {formatted.isActive}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">{building._count?.floors || 0}</Text>
                  </td>
                  <td>
                    <Text size="sm">{(building as any).roomsCount || 0}</Text>
                  </td>
                  <td>
                    <Text size="sm">{(building as any).holidaysCount || 0}</Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {formatted.createdAt}
                    </Text>
                  </td>
                  <td>
                    <Group gap="xs">
                      <Tooltip label="View Details">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => onSelectBuilding(building.id)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      
                      <Tooltip label="Edit Building">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => onEditBuilding(building.id)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      
                      <Menu position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconTrash size={16} />}
                            color="red"
                            onClick={() => setDeleteConfirmId(building.id)}
                          >
                            Delete Building
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Paper>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Group justify="center">
          <Pagination
            value={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </Group>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <Alert
          color="red"
          title="Confirm Deletion"
          withCloseButton
          onClose={() => setDeleteConfirmId(null)}
        >
          <Stack gap="sm">
            <Text>
              Are you sure you want to delete this building? This action cannot be undone.
            </Text>
            <Group>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                color="red"
                onClick={() => handleDelete(deleteConfirmId)}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
